export enum NotificationType {
  BIRTHDAY_REMINDER = 'BIRTHDAY_REMINDER',
  BIRTHDAY_CONGRATULATIONS = 'BIRTHDAY_CONGRATULATIONS',
  NOTIFICATION_CENTER = 'NOTIFICATION_CENTER',
  MARKETPLACE_SALE = 'MARKETPLACE_SALE',
  UNLINK_CLIENT_REMOVED_FROM_ORG = 'UNLINK_CLIENT_REMOVED_FROM_ORG',
  UNLINK_TRAINER_REMOVED_FROM_ORG = 'UNLINK_TRAINER_REMOVED_FROM_ORG',
  SCHEDULE_EVENT_ADDED = 'SCHEDULE_EVENT_ADDED',
  SCHEDULE_EVENT_COMPLETED = 'SCHEDULE_EVENT_COMPLETED',
  INVITATION_ACCEPTED = 'INVITATION_ACCEPTED',
  INVITATION_REJECTED = 'INVITATION_REJECTED',
  DISCONNECTED_FROM_ORG = 'DISCONNECTED_FROM_ORG',
  // client self-initiated leave (distinct from admin-initiated
  // UNLINK_CLIENT_REMOVED_FROM_ORG — "left" vs "was removed")
  CLIENT_LEFT_ORG = 'CLIENT_LEFT_ORG',
  SCHEDULE_NOTE = 'SCHEDULE_NOTE',
  // dunning (§16/§19.9): neuspela naplata (baner/opomena) i suspenzija
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  SUBSCRIPTION_SUSPENDED = 'SUBSCRIPTION_SUSPENDED',
  // MV2-573 §1.2: a recurring membership's card kept failing until dunning
  // exhausted — staff (org admins + the client's trainer), not the client,
  // since a trainer/admin decides manually whether to nudge, disconnect or
  // archive; there is no auto-archive.
  MEMBERSHIP_PAYMENT_SUSPENDED = 'MEMBERSHIP_PAYMENT_SUSPENDED',
  // MV2-573 §3, Task 11 (membership-renewal plan): on each lead day the
  // organization picked (7/3/1) before a card-billed RECURRING membership's
  // next charge — to the client and their trainer(s); owner and front desk
  // get the Task 12 daily digest instead.
  MEMBERSHIP_RENEWAL_REMINDER = 'MEMBERSHIP_RENEWAL_REMINDER',
  // MV2-573 §3, Task 11: on each picked lead day before a membership that
  // does NOT renew (SINGLE, or cardless RECURRING without auto-renew)
  // expires, only when the client has no successor already lined up.
  MEMBERSHIP_EXPIRY_REMINDER = 'MEMBERSHIP_EXPIRY_REMINDER',
  // Task 11: on each picked lead day before a cardless RECURRING membership
  // with auto-renew opens its next cycle on its end date (Decision 5) — the
  // payment is recorded at the front desk, no card is charged.
  MEMBERSHIP_AUTO_RENEWAL_REMINDER = 'MEMBERSHIP_AUTO_RENEWAL_REMINDER',
  // Task 12 (membership-renewal plan, Decision 8) — ONE summary per
  // organization civil day, to the org's OWNER + STAFF (never trainers or
  // clients, who get the per-recipient 7/3/1 reminders above instead): cycles
  // renewing/expiring soon, cycles waiting for payment, and cycles whose
  // visits were used up today. Counts and capped name lists only — no money.
  MEMBERSHIP_DAILY_DIGEST = 'MEMBERSHIP_DAILY_DIGEST',
  // MV2-573 §4.2: a staff member undid a walk-in check-in (correcting a
  // mistake) — notifies the OTHER staff, never the actor.
  MEMBERSHIP_CHECK_IN_VOIDED = 'MEMBERSHIP_CHECK_IN_VOIDED',
  // Same event as MEMBERSHIP_CHECK_IN_VOIDED, addressed to the CLIENT whose
  // check-in was removed — separate type so the copy can be phrased in
  // second person instead of the staff notice's third-person wording. Voiding
  // is staff-only, so the client is never the actor here.
  MEMBERSHIP_CHECK_IN_VOIDED_FOR_CLIENT = 'MEMBERSHIP_CHECK_IN_VOIDED_FOR_CLIENT',
  // Task 10 (membership-renewal plan) — a check-in or booking write crossed
  // the cycle's visit limit exactly (consumed went from below the limit to
  // equal it). Sent once per cycle to the client's trainer(s) only — the
  // owner sees it in the Task 12 daily digest instead.
  MEMBERSHIP_VISITS_USED_UP = 'MEMBERSHIP_VISITS_USED_UP',
  // Task 10 — a CLIENT self-service attempt (booking or check-in) was
  // refused because the membership is used up and does not renew on use
  // (a renewable one renews instead of refusing). Real-time, to the
  // trainer(s) AND front desk, deduped per (row, organization civil day) —
  // the client is trying to act right now, a next-day digest is too late.
  MEMBERSHIP_NO_VISITS_LEFT = 'MEMBERSHIP_NO_VISITS_LEFT',
  // MV2-573 §1.2: a trainer-scheduled first charge attempt for a RECURRING
  // membership run "on trust" fired and failed (most commonly: still no
  // saved card) — staff-only, never auto-retried; the trainer re-schedules.
  MEMBERSHIP_SCHEDULED_ACTIVATION_FAILED = 'MEMBERSHIP_SCHEDULED_ACTIVATION_FAILED',
  // A membership's EFFECTIVE Terms of Use changed (org default or the
  // membership's own trainer override) — every client on that RECURRING
  // membership must re-accept, or billing pauses at the next renewal
  // (`BillingRunnerService.chargeOne`'s TOU gate) until they do.
  MEMBERSHIP_TOU_UPDATED = 'MEMBERSHIP_TOU_UPDATED',
  // MV2-573 §1: a RECURRING membership was assigned with requiresCardLinkNow
  // — the client, and only the client (staff already saw their own
  // assignment action), gets nudged to link a card before the 72h
  // PENDING_PAYMENT_METHOD row auto-expires (expirePendingPaymentMethodMemberships).
  MEMBERSHIP_CARD_SETUP_REQUIRED = 'MEMBERSHIP_CARD_SETUP_REQUIRED',
  // …and when those 72h run out without a card, the row expires: the org's
  // owners, desk and the client's trainers hear about it (in-app + e-mail)
  // instead of finding out when the client turns up at the desk.
  MEMBERSHIP_CARD_SETUP_EXPIRED = 'MEMBERSHIP_CARD_SETUP_EXPIRED',
  // booking (MV2-394): a trainer cancelled an event people had booked
  BOOKING_EVENT_CANCELLED = 'BOOKING_EVENT_CANCELLED',
  // a seat came free and the queue's next in line may claim it, until the
  // offer window lapses
  BOOKING_WAITLIST_SEAT_AVAILABLE = 'BOOKING_WAITLIST_SEAT_AVAILABLE',
  // the client's seat is confirmed
  BOOKING_CONFIRMED = 'BOOKING_CONFIRMED',
  // an event people had booked was moved or otherwise changed — without this a
  // client keeps the old time and simply misses the class
  BOOKING_EVENT_UPDATED = 'BOOKING_EVENT_UPDATED',
  // ahead of the session, to both the client and the trainers running it
  BOOKING_REMINDER = 'BOOKING_REMINDER',
  // same as BOOKING_REMINDER but fired again closer to the start, as its own
  // independent window/marker so the two never suppress each other
  BOOKING_REMINDER_1H = 'BOOKING_REMINDER_1H',
  // "you can still cancel free of charge" — anchored to the cancellation
  // DEADLINE rather than the event start, and only for bookings actually at
  // risk of an online charge (see BOOKING_ONLINE_PAYMENT_PENDING_STATUSES)
  BOOKING_CANCELLATION_DEADLINE_REMINDER = 'BOOKING_CANCELLATION_DEADLINE_REMINDER',
  BOOKING_CANCELLATION_DEADLINE_REMINDER_1H = 'BOOKING_CANCELLATION_DEADLINE_REMINDER_1H',
  // Daily Activity (in-app only, this phase — no push yet): per-user
  // configurable morning/evening reminder to open the entry modal
  DAILY_ACTIVITY_REMINDER_MORNING = 'DAILY_ACTIVITY_REMINDER_MORNING',
  DAILY_ACTIVITY_REMINDER_EVENING = 'DAILY_ACTIVITY_REMINDER_EVENING',
  // a client checked "Share with your trainer" on a Daily Activity entry
  DAILY_ACTIVITY_SHARED_WITH_TRAINER = 'DAILY_ACTIVITY_SHARED_WITH_TRAINER',
  // marketplace, buyer side: the paid content landed in Saved (a seat purchase
  // uses BOOKING_CONFIRMED instead), and a refund the platform issued was
  // confirmed by the processor
  MARKETPLACE_PURCHASE_DELIVERED = 'MARKETPLACE_PURCHASE_DELIVERED',
  MARKETPLACE_PURCHASE_REFUNDED = 'MARKETPLACE_PURCHASE_REFUNDED',
  // marketplace, seller side: the payout for a sale actually left the
  // platform (PayoutStatus.PAID) — distinct from MARKETPLACE_SALE, which
  // fires when the sale itself happens, long before the money moves
  MARKETPLACE_PAYOUT_PAID = 'MARKETPLACE_PAYOUT_PAID',
  // a trainer saved Terms of Use of their own while the organization lets
  // trainers have them — addressed to the owner
  TRAINER_OWN_TERMS_SAVED = 'TRAINER_OWN_TERMS_SAVED',
  // marketplace, seller side: a platform admin removed a listing and denied the
  // seller access to the marketplace (6.8) — carries the admin's reason
  MARKETPLACE_ACCESS_DENIED = 'MARKETPLACE_ACCESS_DENIED',
  // MVP client notices (in-app only), to the client whose membership staff
  // just paused / cancelled / renewed by hand — second person, the client is
  // never the actor here
  MEMBERSHIP_PAUSED = 'MEMBERSHIP_PAUSED',
  MEMBERSHIP_CANCELLED = 'MEMBERSHIP_CANCELLED',
  MEMBERSHIP_RENEWED_MANUALLY = 'MEMBERSHIP_RENEWED_MANUALLY',
  // a visit was taken off the client's membership by a check-in (staff-recorded)
  MEMBERSHIP_VISIT_DEDUCTED = 'MEMBERSHIP_VISIT_DEDUCTED',
  // a client gave up their own seat — tells the event's trainer(s), the
  // counterpart of BOOKING_EVENT_CANCELLED (trainer cancels the whole event)
  BOOKING_CANCELLED_BY_CLIENT = 'BOOKING_CANCELLED_BY_CLIENT',
  // 6.11 — a client's PAR-Q has a stopping answer: to the owner(s) and the
  // client's trainer(s), who must let them in before they book or train
  HEALTH_SCREENING_HOLD = 'HEALTH_SCREENING_HOLD',
}

export enum NotificationRecurrenceType {
  EVERY_N_DAYS = 'EVERY_N_DAYS',
  FIRST_OF_MONTH = 'FIRST_OF_MONTH',
  CUSTOM = 'CUSTOM',
}

export enum NotificationSendVia {
  EMAIL = 'EMAIL',
  IN_APP = 'IN_APP',
  BROADCAST_MESSAGE = 'BROADCAST_MESSAGE',
}

export enum NotificationTemplateStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}
